import * as React from 'react';
import ReactDOM from 'react-dom';
import * as PopperJS from '@popperjs/core';
import { usePopper } from 'react-popper';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';
import { FocusLock } from '../../atoms/FocusLock';
import { Surface, SurfaceProps } from '../../atoms/Surface';

// Todo: Should popper be rejecting outside clicks?
type BaseProps = SurfaceProps & {
    onClose: (e: React.MouseEvent | React.KeyboardEvent | React.TouchEvent | undefined) => void
    placement?: PopperJS.Placement
    menu?: boolean
    width?: string | number;
    className?: string
}

type PopperSurfaceProps = BaseProps & {
    open?: boolean
    className?: string
    containerRef?: React.RefObject<HTMLElement> | HTMLElement
    triggerRef: React.RefObject<HTMLElement> | HTMLElement | PopperJS.VirtualElement | null,
    disableScrim?: true
}

type PopperInnerProps = BaseProps & {
    triggerElement: HTMLElement | PopperJS.VirtualElement | null
}

const Scrim = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: none;
    z-index: ${props => props.theme.zIndex.modal};
`;

const StyledSurface = styled(Surface) <{ menu?: boolean }>`
    z-index: ${props => props.theme.zIndex.modal};

    ${props => props.menu && css`
        padding: ${props.theme.spacing(1, 0)};  
    `}

    /* Fade Animations */
    /* Larger UI panels and tooltips */
	&.fade-appear,
    &.fade-enter {
        opacity: 0;
    }
    &.fade-appear-active,
    &.fade-enter-active,
    &.fade-appear-done,
    &.fade-enter-done {
        opacity: 1;
        transition: opacity ${props => props.theme.transitions.short};
    }
    &.fade-exit {
        opacity: 1;
    }
    &.fade-exit-active,
    &.fade-exit-done {
        opacity: 0;
        transition: opacity ${props => props.theme.transitions.short};
    }
`;

const PopperSurface: React.FC<PopperSurfaceProps> = (props) => {
    const { className, open, containerRef, triggerRef, disableScrim, ...innerProps } = props;
    const [triggerElement, setTriggerElement] = React.useState<HTMLElement | PopperJS.VirtualElement | null>(null);
    const [containerElement, setContainerElement] = React.useState<HTMLElement | null>(null);

    React.useEffect(() => {
        if (containerRef instanceof HTMLElement) setContainerElement(containerRef);
        else if (containerRef && "current" in containerRef) setContainerElement(containerRef.current);
    }, [triggerRef]);

    React.useEffect(() => {
        if (triggerRef instanceof HTMLElement) setTriggerElement(triggerRef);
        else if (triggerRef && "current" in triggerRef) setTriggerElement(triggerRef.current);
        else { setTriggerElement(triggerRef) }
    }, [triggerRef]);
    const onClick = React.useCallback((event: React.MouseEvent | React.TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();
        innerProps.onClose?.(event)
    }, [innerProps.onClose]);
    const onKeyUp = React.useCallback((event: React.KeyboardEvent) => {
        if (event.key == "Escape")
            innerProps.onClose?.(event)
    }, [innerProps.onClose]);
    const animationEnded = (node: HTMLElement, done: () => void) => node.addEventListener("transitionend", done, false);
    //onKeyUp workd here becaus of FocusLock in child
    const content = <div onKeyUp={onKeyUp}>
        {open && !disableScrim && <Scrim onClick={onClick} onTouchStart={onClick} /> || null}
        <CSSTransition in={open} appear classNames="fade" addEndListener={animationEnded} unmountOnExit>
            <PopperInner className={props.className} triggerElement={triggerElement} {...innerProps} />
        </CSSTransition>
    </div>

    return containerElement ? ReactDOM.createPortal(content, containerElement) : content;
}

const PopperInner: React.FC<PopperInnerProps> = (props) => {
    const { triggerElement, onClose, elevation = 2, children, placement, width, ...surfaceProps } = props;
    const [popperElement, setPopperElement] = React.useState<HTMLDivElement | null>(null);
    const { styles, attributes, update } = usePopper(triggerElement, popperElement, {
        strategy: "fixed",
        placement: placement,

    });
    React.useEffect(() => {
        if (update)
            update();
    }, [children])

    return <StyledSurface ref={setPopperElement} style={styles.popper} elevation={elevation} {...surfaceProps} {...attributes.popper}>
        <FocusLock autoFocus style={{ height: "100%", minWidth: width }}>
            {children}
        </FocusLock>
    </StyledSurface>
}

type PopperSectionProps = {
    menu?: boolean
}
const PopperSection = styled.div<PopperSectionProps>`
    padding: ${props => props.menu ? props.theme.spacing(1, 0) : props.theme.spacing(2)};
    :not(:last-child) {
        border-bottom: 1px solid ${props => props.theme.palette.ui.divider};
    }
    :first-child {
        padding-top: ${props => props.menu ? "0" : props.theme.spacing(1)};
    }
    :last-child {
        padding-bottom: ${props => props.menu ? "0" : props.theme.spacing(1)};
    }
`;

const PopperSectionHeader = styled.div`
    ${props => props.theme.typography.body3};
    color: ${props => props.theme.palette.text.disabled};
    padding: ${props => props.theme.spacing(0, 2)};
    margin-bottom: ${props => props.theme.spacing(0.5)};
    text-transform: uppercase;
    font-weight: ${props => props.theme.typography.bold};
`;

const PopperQuickActions = styled(PopperSection)`
    padding: ${props => props.theme.spacing(1)};
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
`;

export default PopperSurface;
export {
    PopperSurface, PopperSurfaceProps,
    PopperSection, PopperSectionProps,
    PopperSectionHeader, PopperQuickActions
}
